'use client';

import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { themeClasses } from '../theme/tokens';

/**
 * Props for the ToggleCell component
 */
interface ToggleCellProps {
  /**
   * Current toggle state
   * - true: Switch is ON (blue background, text shows "ON")
   * - false: Switch is OFF (gray background, text shows "OFF")
   */
  value: boolean;

  /**
   * Callback function when toggle state changes
   * Supports both synchronous and asynchronous operations
   *
   * During async operations, the toggle enters a processing state:
   * - Shows reduced opacity (opacity-50)
   * - Disables user interaction
   * - Cursor changes to not-allowed
   *
   * @param value - The new toggle state
   * @returns void or Promise<void>
   *
   * @example
   * // Synchronous
   * onChange={(value) => setEnabled(value)}
   *
   * @example
   * // Asynchronous (e.g., API call)
   * onChange={async (value) => {
   *   await updateMailboxStatus(id, value);
   *   toast.success('Status updated');
   * }}
   */
  onChange: (value: boolean) => void | Promise<void>;

  /**
   * Whether the toggle is disabled
   * When true:
   * - Toggle cannot be clicked
   * - Shows reduced opacity
   * - Cursor changes to not-allowed
   *
   * @default false
   */
  disabled?: boolean;

  /**
   * Custom ARIA label for accessibility
   * If not provided, defaults to "Enable" or "Disable" based on current state
   *
   * @example "Toggle mailbox status"
   */
  ariaLabel?: string;

  /**
   * Additional CSS classes for the container
   */
  className?: string;

  /**
   * Horizontal alignment of the toggle within the cell
   *
   * - 'left': Aligns toggle to the left (justify-start)
   * - 'center': Centers the toggle (justify-center)
   * - 'right': Aligns toggle to the right (justify-end)
   *
   * This should match the column's alignment prop for visual consistency
   *
   * @default 'center'
   */
  align?: 'left' | 'center' | 'right';
}

/**
 * ToggleCell Component
 *
 * A reusable toggle switch cell component for data tables with ON/OFF text labels.
 * Provides an accessible, animated switch with support for async operations.
 *
 * ## Features
 *
 * - **Visual States:**
 *   - ON: Blue background (primary-600), white knob, dark "ON" text
 *   - OFF: Gray background (slate-300), white knob, muted "OFF" text
 *   - Processing: Reduced opacity, disabled interaction
 *   - Disabled: Reduced opacity, cursor not-allowed
 *
 * - **Text Labels:**
 *   - Shows "ON" or "OFF" text next to the switch
 *   - ON text is darker (slate-900) for better visibility
 *   - OFF text is lighter (slate-500) for subtle appearance
 *
 * - **Alignment Support:**
 *   - Supports left, center, or right alignment
 *   - Should match the column's alignment for visual consistency
 *
 * - **Async Operation Handling:**
 *   - Automatically manages processing state during async operations
 *   - Prevents duplicate clicks while processing
 *   - Shows visual feedback (opacity reduction)
 *
 * - **Event Handling:**
 *   - Stops event propagation to prevent row click events
 *   - Prevents action when disabled or processing
 *
 * ## Accessibility
 *
 * - Uses `role="switch"` for proper screen reader support
 * - `aria-checked` attribute reflects current state
 * - Custom `aria-label` support
 * - Keyboard accessible (can be focused and toggled with spacebar/enter)
 * - Disabled state properly announced
 *
 * ## Animation
 *
 * - Smooth 200ms transitions for:
 *   - Background color changes
 *   - Knob position (translateX)
 *   - All state changes
 *
 * ## Usage in DataTable
 *
 * Define a toggle column in your column definitions:
 *
 * ```tsx
 * const columns: ColumnDefinition<TData>[] = [
 *   {
 *     id: 'enabled',
 *     label: 'Enabled Status',
 *     accessor: 'enabled',
 *     type: 'toggle',
 *     width: '15%',
 *     align: 'right',  // Toggle will align to the right
 *     meta: {
 *       onChange: async (row: TData, value: boolean) => {
 *         await updateStatus(row.id, value);
 *         toast.success('Status updated');
 *       },
 *       disabled: (row: TData) => row.isLocked,
 *     },
 *   },
 * ];
 * ```
 *
 * ## Styling
 *
 * - Uses Tailwind utilities for consistent styling
 * - Inherits theme colors from DataTable theme system
 * - Responsive: Works on all screen sizes
 *
 * @component
 * @example
 * // Basic usage
 * <ToggleCell
 *   value={isEnabled}
 *   onChange={setIsEnabled}
 * />
 *
 * @example
 * // With async operation
 * <ToggleCell
 *   value={mailbox.enabled}
 *   onChange={async (value) => {
 *     await updateMailboxStatus(mailbox.id, value);
 *     refetch();
 *   }}
 *   ariaLabel={`Toggle ${mailbox.email}`}
 * />
 *
 * @example
 * // With alignment
 * <ToggleCell
 *   value={item.active}
 *   onChange={handleToggle}
 *   align="right"
 * />
 */
export const ToggleCell: React.FC<ToggleCellProps> = ({
  value,
  onChange,
  disabled = false,
  ariaLabel,
  className,
  align = 'center',
}) => {
  // Track processing state for async operations
  const [isProcessing, setIsProcessing] = useState(false);

  /**
   * Handles toggle click
   * - Stops event propagation to prevent row click
   * - Returns early if disabled or processing
   * - Manages processing state for async operations
   * - Calls onChange with inverted value
   */
  const handleToggle = async (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click

    if (disabled || isProcessing) return;

    setIsProcessing(true);
    try {
      await onChange(!value);
    } finally {
      setIsProcessing(false);
    }
  };

  // Determine horizontal alignment class
  const justifyClass = align === 'left' ? 'justify-start' : align === 'right' ? 'justify-end' : 'justify-center';

  return (
    <div className={cn('flex items-center gap-2', justifyClass, className)}>
      {/* Toggle Switch Button */}
      <button
        type="button"
        className={cn(
          // Base styles: relative positioning for inner knob, flex layout, size, rounded corners, smooth transitions
          'relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-200',
          // Focus styles from theme
          themeClasses.focus,
          // Color based on enabled state
          value
            ? 'bg-primary-600 hover:bg-primary-700'  // ON: Blue background
            : 'bg-slate-300 hover:bg-slate-400',      // OFF: Gray background
          // Processing/disabled state: reduced opacity, no pointer events
          (isProcessing || disabled)
            ? 'opacity-50 cursor-not-allowed'
            : 'cursor-pointer'
        )}
        onClick={handleToggle}
        disabled={isProcessing || disabled}
        role="switch"
        aria-checked={value}
        aria-label={ariaLabel || (value ? 'Disable' : 'Enable')}
      >
        {/* Toggle Knob (white circle that slides) */}
        <span
          className={cn(
            // Base styles: inline-block, size, rounded, white background, shadow, smooth slide animation
            'inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200',
            // Position based on state:
            // OFF (value=false): translateX(4px) - positioned to the left
            // ON (value=true): translateX(24px) - positioned to the right
            value ? 'translate-x-6' : 'translate-x-1'
          )}
        />
      </button>

      {/* ON/OFF Text Label */}
      <span
        className={cn(
          'text-xs font-medium',
          // Color based on state:
          // ON: Darker text (slate-900) for emphasis
          // OFF: Lighter text (slate-500) for subtle appearance
          value ? 'text-slate-900' : 'text-slate-500'
        )}
      >
        {value ? 'ON' : 'OFF'}
      </span>
    </div>
  );
};
